import type { CSSProperties, ReactNode } from "react";
import { cn } from "./cn";

export const BADGE_VARIANTS = ["soft", "solid", "outline"] as const;

export type BadgeVariant = (typeof BADGE_VARIANTS)[number];

export const BADGE_TONES = {
  neutral: {
    soft: "bg-subtle text-content-secondary",
    solid: "bg-content-muted text-app",
    outline: "border border-line text-content-secondary",
  },
  primary: {
    soft: "bg-primary-500/15 text-primary-500",
    solid: "bg-primary-500 text-white",
    outline: "border border-primary-500 text-primary-500",
  },
  success: {
    soft: "bg-success/15 text-success",
    solid: "bg-success text-white",
    outline: "border border-success text-success",
  },
  warning: {
    soft: "bg-warning/15 text-warning",
    solid: "bg-warning text-white",
    outline: "border border-warning text-warning",
  },
  error: {
    soft: "bg-error/15 text-error",
    solid: "bg-error text-white",
    outline: "border border-error text-error",
  },
  info: {
    soft: "bg-info/15 text-info",
    solid: "bg-info text-white",
    outline: "border border-info text-info",
  },
} as const satisfies Record<string, Record<BadgeVariant, string>>;

export type Tone = keyof typeof BADGE_TONES;

export const BADGE_SIZE_CLASSES = {
  sm: "px-1.5 py-0.5 text-[10px]",
  md: "px-2 py-0.5 text-xs",
} as const satisfies Record<string, string>;

export type BadgeSize = keyof typeof BADGE_SIZE_CLASSES;

export interface BadgeProps {
  children: ReactNode;
  tone?: Tone;
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function Badge({
  children,
  tone = "neutral",
  variant = "soft",
  size = "md",
  dot = false,
  className,
  style,
}: BadgeProps) {
  return (
    <span
      style={style}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium",
        BADGE_SIZE_CLASSES[size],
        BADGE_TONES[tone][variant],
        className,
      )}
    >
      {dot ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" /> : null}
      {children}
    </span>
  );
}

export interface ChipProps extends Omit<BadgeProps, "dot"> {
  onRemove?: () => void;
  removeLabel?: string;
}

export function Chip({
  children,
  tone = "neutral",
  variant = "soft",
  size = "md",
  className,
  onRemove,
  removeLabel = "Quitar",
}: ChipProps) {
  return (
    <Badge tone={tone} variant={variant} size={size} className={cn("pr-1", className)}>
      {children}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel}
          className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-content/15"
        >
          <i className="ri-close-line text-xs" />
        </button>
      ) : null}
    </Badge>
  );
}
